import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, Copy, Check, ArrowLeft, ExternalLink, ShieldAlert, FileText } from "lucide-react";
import { ASSETS } from "@/lib/constants";
import FilingStatusBar from "@/components/bdm/FilingStatusBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "grc_bdm_token";

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

function CopyButton({ text, label, testid, disabled }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      disabled={disabled || !text}
      data-testid={testid}
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setDone(true);
        toast.success("Copied — paste it into the form");
        setTimeout(() => setDone(false), 1800);
      }}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {done ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {label}
    </button>
  );
}

export const FilingPacket = ({ authHeader, adminBdmId }) => {
  const [month, setMonth] = useState("");
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const url = adminBdmId
        ? `${API}/admin/bdm-packet?bdm_id=${adminBdmId}${m ? `&month=${m}` : ""}`
        : `${API}/bdm/packet${m ? `?month=${m}` : ""}`;
      const { data } = await axios.get(url, { headers: authHeader() });
      setPacket(data);
      if (!m) setMonth(data.month);
    } catch {
      setPacket(null);
    } finally {
      setLoading(false);
    }
  }, [adminBdmId, authHeader]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [adminBdmId]);

  if (loading) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
      </div>
    );
  }
  if (!packet) {
    return <p className="text-sm text-ink-500">Could not build the packet.</p>;
  }

  const fullText = packet.sections
    .filter((s) => !s.empty)
    .map((s) => `${s.label.toUpperCase()}\n${s.text}`)
    .join("\n\n");

  return (
    <div data-testid="bdm-packet">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <select
            value={month}
            onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
            data-testid="bdm-packet-month"
            className="rounded-full border border-linen-border bg-white px-3 py-1.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-teal/40"
          >
            {(packet.months || [packet.month]).map((m) => (
              <option key={m} value={m}>{monthLabel(m)}</option>
            ))}
          </select>
          <span className="text-xs text-ink-500 tabular-nums" data-testid="bdm-packet-total">
            {packet.total} logged
          </span>
        </div>
        <CopyButton text={fullText} label="Copy everything" testid="bdm-packet-copy-all" />
      </div>

      {packet.filing && (
        <div className="mt-5">
          <FilingStatusBar
            month={packet.month}
            filing={packet.filing}
            authHeader={authHeader}
            readOnly={!!adminBdmId}
            onChange={(f) => setPacket((p) => ({ ...p, filing: { ...p.filing, ...f } }))}
          />
        </div>
      )}

      {packet.total === 0 ? (
        <div className="mt-5 rounded-2xl bg-linen/70 border border-linen-border p-5" data-testid="bdm-packet-empty">
          <p className="text-sm text-navy font-medium">Nothing logged for {monthLabel(month)}.</p>
          <p className="mt-1 text-sm text-ink-500">
            There's nothing to write up yet. Add what you did from the dashboard — even a few lines beats
            filing a blank form.
          </p>
        </div>
      ) : (
        <>
          {packet.suggested_scope?.length > 0 && (
            <section className="mt-5 rounded-2xl bg-white border border-linen-border p-5" data-testid="bdm-packet-scope">
              <p className="text-[11px] uppercase tracking-widest text-teal">Section 1 — scope of work</p>
              <p className="mt-1 text-sm text-ink-500">Tick these boxes based on what you logged:</p>
              <ul className="mt-3 space-y-1.5">
                {packet.suggested_scope.map((s) => (
                  <li key={s} className="flex items-start gap-2 text-sm text-navy">
                    <Check className="w-4 h-4 text-teal mt-0.5 shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="mt-4 space-y-4">
            {packet.sections.map((s) => (
              <section
                key={s.key}
                className={`rounded-2xl border p-5 ${s.empty ? "bg-linen/50 border-linen-border" : "bg-white border-linen-border"}`}
                data-testid={`bdm-packet-section-${s.key}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-teal">Section 2 — {s.label}</p>
                    <p className="text-xs text-ink-400 mt-0.5">{s.count} entr{s.count === 1 ? "y" : "ies"}</p>
                  </div>
                  {!s.empty && (
                    <div className="flex flex-wrap gap-2">
                      <CopyButton text={s.sentence} label="Copy summary" testid={`bdm-packet-copy-sentence-${s.key}`} />
                      <CopyButton text={s.text} label="Copy with detail" testid={`bdm-packet-copy-text-${s.key}`} />
                    </div>
                  )}
                </div>
                {s.empty ? (
                  <p className="mt-3 text-sm text-ink-500 italic">
                    Nothing logged here. Write “N/A” on the form rather than leaving it blank.
                  </p>
                ) : (
                  <>
                    <p className="mt-3 text-sm text-navy leading-relaxed">{s.sentence}</p>
                    <ul className="mt-2 space-y-1">
                      {s.bullets.map((b, i) => (
                        <li key={i} className="text-xs text-ink-500 leading-relaxed">— {b}</li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            ))}
          </div>

          {packet.suggested_notes && (
            <section className="mt-4 rounded-2xl bg-white border border-linen-border p-5" data-testid="bdm-packet-notes">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="text-[11px] uppercase tracking-widest text-teal">Additional notes</p>
                <CopyButton text={packet.suggested_notes} label="Copy notes" testid="bdm-packet-copy-notes" />
              </div>
              <p className="mt-3 text-sm text-navy leading-relaxed">{packet.suggested_notes}</p>
            </section>
          )}

          {packet.evidence?.length > 0 && (
            <section className="mt-4 rounded-2xl bg-white border border-linen-border p-5" data-testid="bdm-packet-evidence">
              <p className="text-[11px] uppercase tracking-widest text-teal">Evidence links</p>
              <p className="mt-1 text-sm text-ink-500">
                Screenshot these if the form asks for backup.
              </p>
              <ul className="mt-3 space-y-1.5">
                {packet.evidence.map((e, i) => (
                  <li key={i} className="text-sm">
                    <a href={e.url} target="_blank" rel="noreferrer" className="text-teal hover:underline inline-flex items-center gap-1">
                      {e.occurred_on} — {e.title} <ExternalLink className="w-3 h-3" />
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <div className="mt-5 rounded-2xl bg-amber-50 border border-amber-300 p-5" data-testid="bdm-packet-respa">
        <p className="flex items-start gap-2 text-sm font-semibold text-amber-900">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          Read it before you sign it.
        </p>
        <p className="mt-1.5 text-sm text-amber-900/90 leading-relaxed">
          This is a summary of your own log — nothing is submitted for you. Your signature on NEXA's form
          is a personal attestation that the services described were actually performed, so check every
          line reads true before you sign.
        </p>
      </div>
    </div>
  );
};

export default function BDMPacket() {
  const nav = useNavigate();
  const authHeader = useCallback(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) nav("/bdmlogin", { replace: true });
  }, [nav]);

  return (
    <div className="min-h-screen bg-linen text-navy antialiased" data-testid="bdm-packet-page">
      <header className="bg-white border-b border-linen-border">
        <div className="container-grc h-16 flex items-center justify-between">
          <Link to="/bdmdashboard" className="flex items-center gap-2">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" />
          </Link>
          <Link
            to="/bdmdashboard"
            data-testid="bdm-packet-back"
            className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-navy"
          >
            <ArrowLeft size={14} /> Back to my dashboard
          </Link>
        </div>
      </header>

      <main className="container-grc py-10 sm:py-14 max-w-4xl">
        <div className="mb-6">
          <p className="text-[11px] uppercase tracking-widest text-teal">Month-end packet</p>
          <h1 className="mt-1 font-heading font-medium text-3xl sm:text-4xl text-navy">
            Your report, already written.
          </h1>
          <p className="mt-2 text-base text-ink-500">
            Every field of NEXA's verification form, filled in from what you logged. Copy each box across,
            attach your backup, read it, sign it.
          </p>
          <a
            href="/partnerreports"
            target="_blank"
            rel="noreferrer"
            data-testid="bdm-packet-report-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold bg-lime text-navy px-4 py-2 rounded-full hover:brightness-95"
          >
            <FileText size={14} /> Open the activity report form
          </a>
        </div>

        <FilingPacket authHeader={authHeader} />
      </main>

      <Toaster position="top-center" richColors />
    </div>
  );
}
